import { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Loader2, CheckCircle, XCircle, ArrowRight, LogIn, UserPlus } from 'lucide-react'
import { getInvitationPreview, redeemInvitation } from '../services/rooms'
import type { InvitationPreview } from '../services/rooms'

export default function JoinPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [preview, setPreview] = useState<InvitationPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [joining, setJoining] = useState(false)
  const [joined, setJoined] = useState(false)

  // Recarrega preview quando o token muda ou usuário volta do login
  useEffect(() => {
    if (!token) {
      setError('Token de convite inválido')
      setLoading(false)
      return
    }
    loadPreview()
  }, [token])

  const loadPreview = async () => {
    try {
      setLoading(true)
      const data = await getInvitationPreview(token!)
      setPreview(data)
      if (!data.is_valid) {
        setError('Este convite não é mais válido (expirado ou já utilizado).')
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Convite não encontrado')
    } finally {
      setLoading(false)
    }
  }

  const handleJoin = async () => {
    if (!token) return
    try {
      setJoining(true)
      await redeemInvitation(token)
      setJoined(true)
      setTimeout(() => {
        navigate(`/?room=${preview?.room_id}`)
      }, 1500)
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Erro ao aceitar convite'
      if (msg.includes('já é membro')) {
        setJoined(true)
        setTimeout(() => {
          navigate(`/?room=${preview?.room_id}`)
        }, 1500)
      } else {
        setError(msg)
      }
    } finally {
      setJoining(false)
    }
  }

  const isLoggedIn = !!localStorage.getItem('token')
  const redirectPath = location.pathname + location.search

  if (loading) {
    return (
      <div
        className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center"
        role="status"
        aria-label="Carregando convite"
      >
        <Loader2 size={32} className="animate-spin text-agent" aria-hidden="true" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-md w-full p-8">
        {joined ? (
          <div className="text-center">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Você entrou no projeto!
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Redirecionando para {preview?.room_name}...
            </p>
          </div>
        ) : error && !preview ? (
          <div className="text-center">
            <XCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Convite inválido
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-agent/10 flex items-center justify-center mx-auto mb-3">
                <span className="text-agent font-bold text-lg">
                  {preview?.room_name?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                Convite para {preview?.room_name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {preview?.inviter_name
                  ? `Convidado por ${preview.inviter_name}`
                  : 'Você foi convidado para participar deste projeto'}
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Função:</span>
                <span className="font-medium text-gray-900 dark:text-white capitalize">{preview?.role}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Expira em:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {preview?.expires_at
                    ? new Date(preview.expires_at).toLocaleDateString('pt-BR')
                    : 'Nunca'}
                </span>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {isLoggedIn ? (
              <button
                onClick={handleJoin}
                disabled={joining || !!error}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-agent text-white text-sm font-medium hover:bg-agent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {joining ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    Aceitar Convite
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Entre com sua conta ou crie uma para participar.
                </p>
                <button
                  onClick={() => navigate('/login', { state: { redirectTo: redirectPath } })}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-agent text-white text-sm font-medium hover:bg-agent-dark transition-colors cursor-pointer"
                >
                  <LogIn size={16} />
                  Entrar
                </button>
                <button
                  onClick={() => navigate('/register', { state: { redirectTo: redirectPath } })}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <UserPlus size={16} />
                  Criar conta
                </button>
              </div>
            )}

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full mt-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              Voltar ao Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  )
}
